<template>
  <div class="flex flex-col gap-3">
    <TTipbox
      v-if="externalAuthError"
      variant="error"
    >
      <!-- eslint-disable vue/no-v-html -->
      <span v-html="externalAuthErrorMessage" />
    </TTipbox>

    <TOverlay :show="isExternalAuthenticationLoading">
      <div class="flex flex-col gap-3 whitespace-nowrap">
        <TButton
          variant="secondary"
          thin
          :class="computedButtonClasses"
          @click="externalAuth('google')"
        >
          <TIconCustom
            name="google-logo"
            size="lg"
          />
          <span>{{ t('continueWith', { provider: 'Google' }) }}</span>
        </TButton>

        <TButton
          variant="secondary"
          thin
          :class="computedButtonClasses"
          @click="externalAuth('apple')"
        >
          <TIconCustom
            name="apple-logo"
            size="lg"
          />
          <span>{{ t('continueWith', { provider: 'Apple' }) }}</span>
        </TButton>

        <TButton
          variant="secondary"
          thin
          :class="computedButtonClasses"
          @click="externalAuth('facebook')"
        >
          <TIconCustom
            name="facebook-logo"
            size="lg"
          />
          <span>{{ t('continueWith', { provider: 'Facebook' }) }}</span>
        </TButton>

        <TDivider>{{ t('or') }}</TDivider>

        <TButton
          v-if="authType !== AUTHENTICATION_TYPE.Login"
          variant="secondary"
          thin
          :class="computedButtonClasses"
         
          @click="handleSignupWithEmail"
        >
          <TIcon
            :icon="['fal', 'envelope']"
            size="lg"
          />
          <span>{{ t('signUpWithEmail') }}</span>
        </TButton>

        <TButton
          v-if="authType !== AUTHENTICATION_TYPE.Signup"
          variant="secondary"
          thin
          :class="computedButtonClasses"
         
          @click="handleLoginWithEmail"
        >
          <TIcon
            :icon="['fal', 'envelope']"
            size="lg"
          />
          <span>{{ t('continueWithEmail') }}</span>
        </TButton>
      </div>
    </TOverlay>
  </div>
</template>

<script setup lang="ts">
import type { AUTHENTICATION_INTENT,
  EXTERNAL_AUTHENTICATION_PROVIDERS } from '~/constants/authentication'
import {
  AUTHENTICATION_TYPE,
} from '~/constants/authentication'
import type { Nullable } from '~/types'

const props = withDefaults(defineProps<{
  authType?: Nullable<AUTHENTICATION_TYPE>
  intent?: AUTHENTICATION_INTENT
  redirectPath?: string
}>(), {
  authType: null,
})

const emit = defineEmits<{
  'show-signup-form': []
  'show-login-form': []
}>()

const { t } = useI18n()

const isImplicitSignup = computed(() => !props.authType || props.authType === 'Signup')

const { triggerExternalAuth } = useExternalAuthProvider()
const { pageSource } = usePageSource()
const { authenticationType, getAuthErrorMessage } = useAuthentication()
const externalAuthError = useExternalAuthError()
const isExternalAuthenticationLoading = useIsExternalAuthenticationLoading()

const externalAuthErrorMessage = getAuthErrorMessage({
  code: computed(() => externalAuthError.value?.code ?? null),
})

async function externalAuth(provider: EXTERNAL_AUTHENTICATION_PROVIDERS) {
  externalAuthError.value = null

  trackCTAClicked({
    cta: authenticationType.value === 'Login' ? `login_${provider}` : `signup_${provider}`,
    pageSource: pageSource.value,
  })

  await triggerExternalAuth({
    provider,
    isSignupEvent: isImplicitSignup.value,
    intent: props.intent,
    redirectPath: props.redirectPath,
  })
}

const computedButtonClasses = computed(() => 'flex items-center justify-center gap-2')

function handleSignupWithEmail() {
  externalAuthError.value = null

  trackCTAClicked({
    cta: 'signup_email',
    pageSource: pageSource.value,
  })

  emit('show-signup-form')
}

function handleLoginWithEmail() {
  externalAuthError.value = null

  trackCTAClicked({
    cta: 'login_email',
    pageSource: pageSource.value,
  })

  emit('show-login-form')
}
</script>

<i18n lang="json">
{
  "en": {
    "continueWithEmail": "Continue with email",
    "continueWith": "Continue with {provider}",
    "signUpWithEmail": "Sign up with email",
    "or": "or"
  },
  "fr": {
    "continueWithEmail": "Continuez par courriel",
    "continueWith": "Continuez avec {provider}",
    "signUpWithEmail": "Inscrivez-vous par courriel",
    "or": "ou"
  }
}
</i18n>
